import { Dropdown, DropdownMenuItemType, IDropdownOption, ISearchBox, SearchBox, Stack } from '@fluentui/react';
import * as React from 'react';
import { APIItem } from '../../interfaces/APIItem';
import { DomainItem } from '../../interfaces/DomainItem';
import { TenantContext, UserContext } from '../../contexts/ContextWrapper';
import { useNavigate } from 'react-router-dom';
import { useContext, useMemo, useState } from 'react';
import NavigationButtonAdmin from '../navigation/NavigationButtonAdmin';
import { NON_DOMAIN_PAGES } from '../../helpers/MenuHelper';

interface SelectionsProps {
	loggedIn: boolean
}

const Selections: React.FC<SelectionsProps> = (props) => {

	const { tenantContext, setTenantContext } = useContext(TenantContext);
	const { userContext } = useContext(UserContext);
	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useState<string>('');
	let searchBoxRef: ISearchBox;

	const backColor = (): string => {
		try {
			switch (tenantContext.api) {
				case 'test':
					return '#e3f0e1'
				case 'dev':
					return '#fbe4e4'
				default:
					return '#f3f2f1'
			}
		} catch (error) {
			return '#f3f2f1'
		}
	}

	const availableDomains = useMemo(() => {
		return userContext.userData.Domains?.filter(data =>
			tenantContext.api.toLowerCase() === data.APIName.toLowerCase()
		) || [];
	}, [userContext.userData.Domains, tenantContext.api]);

	const filteredDomains = useMemo(() => {
		if (!searchValue) {
			return availableDomains;
		}

		const lowerText = searchValue.toLowerCase();
		return availableDomains.filter(item =>
			item.CompanyName.toLowerCase().includes(lowerText) ||
			item.DomainName.toLowerCase().includes(lowerText)
		);
	}, [availableDomains, searchValue]);

	const apiOptions = useMemo(() => {
		return userContext.userData.API?.map((item: APIItem) => {
			return ({
				key: item.APIName.toLowerCase(),
				text: item.APIName,
				data: item.APIUri,
			})
		}) || [];
	}, [userContext.userData.API]);

	const domainOptions = useMemo((): IDropdownOption[] => {
		const domainslist: IDropdownOption[] = filteredDomains.map((item: DomainItem) => {
			return ({
				key: item.DomainName.toLowerCase(),
				text: `${item.CompanyName} (${item.DomainName})`,
				data: item,
			});
		});

		if (availableDomains.length > 10) {
			return [
				{
					key: 'searchbox',
					text: '',
					itemType: DropdownMenuItemType.Header,
				},
				{ key: 'divider', text: '', itemType: DropdownMenuItemType.Divider },
				...domainslist
			] as IDropdownOption[]
		}
		else {
			return domainslist;
		}
	}, [availableDomains, filteredDomains]);

	const onChangeDomain = async (option: IDropdownOption) => {
		setTenantContext(prev => ({
			...prev,
			MyTenant: (option.data as DomainItem).MyTenant,
			tenantName: option.data.DomainName,
			// Reset tenant-specific data so stale values don't linger until UpdateSettings runs
			logo: '',
			settings: {} as any,
			groupIds: [],
			companyGroups: [],
			allGroups: [],
			myGroups: [],
		}))
		const page: string = localStorage.getItem('selectedPage') || 'home'
		navigate(`/${(option.data as DomainItem)?.APIName.toLowerCase()}/${option.data.DomainName.toLowerCase()}/${page}`)
	};

	const onChangeApi = async (option: IDropdownOption) => {
		setSearchValue('')
		if (NON_DOMAIN_PAGES.includes(tenantContext.tenantName?.toLowerCase())) {
			setTenantContext(prev => ({ ...prev, api: option.text, apiUri: option.data }))
			navigate(`/${option.text.toLowerCase()}/${tenantContext.tenantName.toLowerCase()}`)
			return
		}

		const domain: string =
			(tenantContext.tenantName && userContext.userData.Domains?.filter(data => option.text.toLowerCase() === data.APIName.toLowerCase())?.find((d) => d.DomainName.toLowerCase() === tenantContext.tenantName.toLowerCase())?.DomainName.toLowerCase()) ||
			userContext.userData.Domains?.filter(data => option.text.toLowerCase() === data.APIName.toLowerCase())?.[0]?.DomainName.toLowerCase() || '';

		setTenantContext(prev => ({
			...prev,
			api: option.text,
			apiUri: option.data,
			tenantName: domain,
			logo: '',
			settings: {} as any,
			groupIds: [],
			companyGroups: [],
			allGroups: [],
			myGroups: [],
		}))
		const page: string = localStorage.getItem('selectedPage') || 'home'
		if (domain) {
			navigate(`/${option.text.toLowerCase()}/${domain}/${page}`)
		}
	};

	if (!props.loggedIn) {
		return <></>
	}

	if (!userContext.userData.Domains || !userContext.userData.API) {
		return <></>
	}

	const DomainDropdown: JSX.Element = (
		<Dropdown
			placeholder="Select domain"
			aria-label="Domain selection dropdown"
			options={domainOptions}
			selectedKey={tenantContext.tenantName}
			styles={{ root: { width: 360, maxWidth: '90%' } }}
			onChange={(_ev, opt) => onChangeDomain(opt!)}
			onClick={() => { setTimeout(() => { searchBoxRef?.focus(); }, 200) }}
			onRenderOption={(props, defaultRender) => {
				if (props!.itemType === DropdownMenuItemType.Header) {
					return (
						// <Stack style={{ borderBottom: '1px solid #ccc' }} >
						<SearchBox
							aria-label="Searchbox. Use tab to navigate to the search results."
							placeholder="Search"
							value={searchValue}
							onChange={(_ev, val) => setSearchValue(val || '')}
							onClear={() => setSearchValue('')}
							onSearch={(val) => setSearchValue(val || '')}
							componentRef={element => searchBoxRef = element!}
							styles={{ root: { width: '100%', marginTop: 5, border: 0 } }}
						/>
						// </Stack>
					)
				}
				return defaultRender!(props);
			}}
		/>
	)

	const ApiDropdown: JSX.Element = (
		<Dropdown
			placeholder="Select API"
			aria-label="Domain selection dropdown"
			options={apiOptions}
			selectedKey={tenantContext.api}
			styles={{ root: { width: 100, maxWidth: '90%' } }}
			onChange={(_ev, opt) => onChangeApi(opt!)}
		/>
	)

	return (
		<Stack horizontal wrap horizontalAlign="end" styles={{ root: { padding: '10px', backgroundColor: backColor() } }} tokens={{ childrenGap: 15 }}>
			{userContext.adminMode && apiOptions.length > 1 ? ApiDropdown : null}
			{userContext.adminMode && (!userContext.userData.isRehngruppen || domainOptions.length > 1) ? DomainDropdown : null}
			{userContext.adminMode &&
				<NavigationButtonAdmin />
			}
		</Stack>
	)
}
export default Selections